import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTour } from "@/context/TourContext";
import { useTheme } from "@/context/ThemeContext";
import { TOUR_STEPS } from "@/components/tour/tourSteps";
import TourStep from "@/components/tour/TourStep";

const CARD_W = 288; // w-72
const GAP = 20;
const SPOTLIGHT_PAD = 8;

function calcStyle(rect, cardPosition) {
  if (cardPosition === "fixed-corner") return { bottom: 24, right: 24 };
  if (!rect) return { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  if (cardPosition === "right") {
    // Su schermi stretti la card non deve uscire dal viewport: prima prova a
    // stare a destra, poi a sinistra, infine si posiziona sotto l'elemento.
    const cardWidth = Math.min(CARD_W, window.innerWidth - GAP * 2);
    if (rect.right + GAP + cardWidth <= window.innerWidth - GAP) {
      return {
        top: Math.max(GAP, rect.top + rect.height / 2),
        left: rect.right + GAP,
        transform: "translateY(-50%)",
      };
    }
    if (rect.left - GAP - cardWidth >= GAP) {
      return {
        top: Math.max(GAP, rect.top + rect.height / 2),
        left: rect.left - GAP - cardWidth,
        transform: "translateY(-50%)",
      };
    }
    return {
      top: Math.min(window.innerHeight - GAP, rect.bottom + GAP),
      left: Math.max(GAP, Math.min(rect.left, window.innerWidth - cardWidth - GAP)),
    };
  }
  // "below"
  return {
    top: rect.bottom + GAP,
    left: Math.max(GAP, Math.min(rect.left, window.innerWidth - CARD_W - GAP)),
  };
}

// Returns the four-panel coordinates that leave a spotlight hole around rect.
// Returns null when noBackdrop or rect is null (→ render no panels).
function spotlightPanels(rect, noBackdrop) {
  if (noBackdrop || !rect) return null;
  const W = window.innerWidth;
  const H = window.innerHeight;
  const t = Math.max(0, rect.top - SPOTLIGHT_PAD);
  const b = Math.min(H, rect.bottom + SPOTLIGHT_PAD);
  const l = Math.max(0, rect.left - SPOTLIGHT_PAD);
  const r = Math.min(W, rect.right + SPOTLIGHT_PAD);
  return { t, b, l, r };
}

export default function TourOverlay() {
  const { isActive, stepIndex, nextStep, skipTour } = useTour();
  const { language } = useTheme();
  const location = useLocation();
  const [cardStyle, setCardStyle] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  const cleanupElRef = useRef(null);
  const cleanupClickRef = useRef(null);
  const observerRef = useRef(null);
  const debounceRef = useRef(null);

  // Removes all per-step side effects (DOM class, click listener, observer)
  const fullCleanup = useCallback(() => {
    if (cleanupElRef.current) {
      cleanupElRef.current.classList.remove("tour-active");
      cleanupElRef.current = null;
    }
    if (cleanupClickRef.current) {
      cleanupClickRef.current();
      cleanupClickRef.current = null;
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  // Finds the current step's target element, sets up the card position and
  // any action listeners. If the element is not in the DOM yet (e.g. a modal
  // that hasn't mounted), a MutationObserver waits for it.
  const recalc = useCallback(() => {
    fullCleanup();

    const step = TOUR_STEPS[stepIndex];
    if (!step) return;

    // Fixed-corner steps (e.g. form modals) anchor the card at a constant
    // screen position and need zero element targeting. Bail out immediately —
    // no querySelector, no getBoundingClientRect, no MutationObserver, no
    // tour-active class. This completely decouples the tour from Radix UI's
    // Focus Trap and dialog animation, preventing the flickering / input-lock bug.
    if (step.cardPosition === "fixed-corner") {
      setTargetRect(null);
      setCardStyle({ bottom: 24, right: 24 });
      return;
    }

    const el = document.querySelector(step.selector);

    if (!el) {
      // Element not in DOM yet — show card at intended position and wait.
      setTargetRect(null);
      setCardStyle(calcStyle(null, step.cardPosition));

      const obs = new MutationObserver(() => {
        if (document.querySelector(step.selector)) {
          obs.disconnect();
          observerRef.current = null;
          recalc(); // re-enter now that element exists
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
      observerRef.current = obs;
      return;
    }

    el.classList.add("tour-active");
    cleanupElRef.current = el;

    const rect = el.getBoundingClientRect();
    setTargetRect(rect);
    setCardStyle(calcStyle(rect, step.cardPosition));

    // Click-to-advance: wire up a one-shot listener on the element itself
    if (step.advanceOn?.type === "click") {
      const handle = () => nextStep();
      el.addEventListener("click", handle, { once: true });
      cleanupClickRef.current = () => el.removeEventListener("click", handle);
    }
  }, [stepIndex, nextStep, fullCleanup]);

  // ── Effect 1: route-based advance ────────────────────────────────────────
  // Fires whenever the pathname changes; advances if it matches the current
  // step's triggerPath. Intentionally omits isActive/stepIndex from the dep
  // array so the listener always reads the latest values from the effect.
  useEffect(() => {
    if (!isActive) return;
    const step = TOUR_STEPS[stepIndex];
    if (step?.advanceOn?.type === "route" && location.pathname.startsWith(step.advanceOn.path)) {
      nextStep();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ── Effect 2: custom window-event advance ─────────────────────────────────
  // Used for complex actions (e.g. "supplier saved") that can't be detected
  // via a simple click or route change. The component that performs the action
  // fires window.dispatchEvent(new CustomEvent("tour:supplier-saved")) and
  // this effect picks it up without any coupling to the TourContext.
  useEffect(() => {
    if (!isActive) return;
    const step = TOUR_STEPS[stepIndex];
    if (step?.advanceOn?.type !== "event") return;
    const handle = () => nextStep();
    window.addEventListener(step.advanceOn.name, handle, { once: true });
    return () => window.removeEventListener(step.advanceOn.name, handle);
  }, [isActive, stepIndex, nextStep]);

  // Escape chiude il tour senza dover cercare il link nella card. È utile
  // soprattutto su mobile e non modifica i dati già inseriti nel form.
  useEffect(() => {
    if (!isActive) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") skipTour();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive, skipTour]);

  // ── Effect 3: recalc on step / active change + resize ────────────────────
  useEffect(() => {
    if (!isActive) {
      fullCleanup();
      setCardStyle(null);
      setTargetRect(null);
      return;
    }

    // Small delay lets React finish rendering the new step's target element
    const t = setTimeout(recalc, 60);

    const onResize = () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(recalc, 80);
    };
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      clearTimeout(t);
      clearTimeout(debounceRef.current);
      window.removeEventListener("resize", onResize);
      fullCleanup();
    };
  }, [isActive, stepIndex, recalc, fullCleanup]);

  if (!isActive) return null;
  const step = TOUR_STEPS[stepIndex];
  if (!step) return null;

  const panels = spotlightPanels(targetRect, step.noBackdrop);

  return createPortal(
    <>
      {/* Spotlight backdrop: four panels leave a hole exactly around the target.
          This avoids all z-index / stacking-context conflicts — nothing is
          rendered over the target, so it remains clickable without z-index hacks. */}
      {panels && (
        <>
          <div className="tour-backdrop-panel" style={{ top: 0, left: 0, right: 0, height: panels.t }} />
          <div className="tour-backdrop-panel" style={{ top: panels.b, left: 0, right: 0, bottom: 0 }} />
          <div className="tour-backdrop-panel" style={{ top: panels.t, left: 0, width: panels.l, height: panels.b - panels.t }} />
          <div className="tour-backdrop-panel" style={{ top: panels.t, left: panels.r, right: 0, height: panels.b - panels.t }} />
        </>
      )}

      {cardStyle && (
        <TourStep
          step={step}
          stepIndex={stepIndex}
          language={language}
          onSkip={skipTour}
          onNext={nextStep}
          style={cardStyle}
        />
      )}
    </>,
    document.body
  );
}
